/**
 * LLD-XCC-210 / §24.2 rule 22 — "product copy is legally and technically load-bearing." A screen
 * component never writes a user-facing string inline; it looks the string up here. This is the
 * client's OWN copy — headings, field labels, static notes — sourced from Prototype_v2.html's DOM
 * per §24.2c ("read the label out of the DOM, never compose it").
 *
 * What is deliberately NOT here: server error text. `title` and `detail` on a Problem response
 * are themselves copy, and are load-bearing at the SERVER (api/components/schemas/common/
 * Problem.yaml) — the client renders them, it does not re-author them, except for the one case
 * §33.6 carves out: an INTERNAL_ERROR / UPSTREAM_UNAVAILABLE response must not surface server
 * detail at all (§9.h B4), so that one fallback string lives here instead, under
 * UNEXPECTED_ERROR_COPY.
 *
 * Populated for the 17 auth-family screens (W140 skeleton pass: a-confirm; W141: the other 16).
 * The remaining 99 units' copy is out of scope for these two passes.
 */

export const AUTH_BRAND = 'Bitbucket Data Protection' as const

export const A_CONFIRM_COPY = {
  heading: 'Confirm your email',
  /** Prototype_v2.html: `We sent a code to <span class="mono">{email}</span>. Enter it to finish
   * creating your account.` — the email is a served/carried value, never composed beyond this
   * template (§24.2c: illustrative values are fixtures, not spec content). */
  leadTemplate: (email: string) => `We sent a code to ${email}. Enter it to finish creating your account.`,
  codeFieldLabel: 'Confirmation code',
  confirmButton: 'Confirm',
  noSignInNote: "Confirming your email doesn't sign you in. You'll sign in next, and set up two-factor authentication as part of it.",
  resendPromptPrefix: "Didn't get a code? ",
  resendLinkLabel: 'Resend it',
  resendPromptSuffix: '.',
  /** A3: "No screen discloses whether an address has an account… a sign-in failure read[s]
   * identically for a known and an unknown address." Generic and non-enumerating on purpose —
   * CREDENTIAL_REJECTED is byte-identical whether the code was wrong or the address never
   * existed, so this copy never says which. */
  codeRejectedCopy: "That code didn't work. Check it and try again.",
} as const

/** §33.6: "An unexpected failure renders a stated error state carrying the correlation
 * identifier… and never a raw message." Used for INTERNAL_ERROR and UPSTREAM_UNAVAILABLE only —
 * every other refusal kind this screen can receive has its own specific rendering. */
export const UNEXPECTED_ERROR_COPY = {
  title: 'Something went wrong',
  detailTemplate: (correlationId: string) => `If this keeps happening, quote this to support: ${correlationId}`,
} as const

export const RATE_LIMITED_COPY = {
  /** RefusalKind.yaml #36: "wait the value the server stated, then one retry… render a waiting
   * state, never an error, and invent no interval of your own." */
  waitingTemplate: (retryAfterSeconds: number) => `Please wait ${retryAfterSeconds}s before trying again.`,
} as const

export const WAF_CHALLENGE_COPY = {
  /** OI-C50's interim fallback — no server-stated wait value exists for this row (unlike
   * RATE_LIMITED_COPY above), so the copy stays deliberately vague on timing. */
  message: 'Too many attempts. Please wait a moment and try again.',
} as const

// ---------------------------------------------------------------------------------------------
// W141 — the remaining 16 auth-family screens. Every string below is transcribed from the
// matching #a-* block in Prototype_v2.html; none is composed (§24.2c).
// ---------------------------------------------------------------------------------------------

export const A_SIGNUP_COPY = {
  heading: 'Create your account',
  lead: 'This account becomes the Owner of a new organisation. Everything below is collected once, here — the organisation is written when you confirm your email on the next step, and nothing is written before that.',
  orgNameLabel: 'Organisation name',
  nameLabel: 'Your name',
  emailLabel: 'Work email',
  passwordLabel: 'Password',
  confirmPasswordLabel: 'Confirm password',
  passwordRule: 'At least 12 characters, with an upper-case letter, a lower-case letter, a number and a symbol. It cannot be one of your last three passwords.',
  orgNameNote: "The organisation name is a display label. It is not an identifier, it is not unique, and it can be changed later — your organisation's permanent identifier is generated for you and never changes.",
  submitButton: 'Create account',
  alreadyHaveAccountPrefix: 'Already have an account? ',
  signInLinkLabel: 'Sign in',
  mfaNote: 'Two-factor authentication is required for every account and is set up when you first sign in.',
  passwordMismatch: "Those two passwords don't match.",
} as const

export const A_SIGNIN_COPY = {
  heading: 'Sign in',
  lead: 'Use the account you created at sign-up, or the one your organisation invited you with.',
  emailLabel: 'Work email',
  passwordLabel: 'Password',
  forgotLinkLabel: 'Forgot your password?',
  signUpLinkLabel: "Don't have an account? Create one",
  submitButton: 'Continue',
  ssoOrLabel: 'OR',
  ssoButton: 'Sign in with your identity provider',
  ssoPlannedChip: 'Planned',
  ssoNote: 'Single sign-on is not available yet. Sign in with your email address and password, and your second factor. SSO arrives in a later release; nothing you set up now has to change when it does.',
  passwordChangedBanner: 'Your password was changed. Sign in with your new password.',
  signedOutEverywhereBanner: 'You changed your password. Every session, including this one, was signed out.',
} as const

export const A_MFA_COPY = {
  heading: 'Enter your verification code',
  leadTemplate: (email: string) => `Six digits from your authenticator app for ${email}.`,
  submitButton: 'Verify',
  noRecoveryCodeNote: 'Codes expire after 30 seconds. There is no recovery-code fallback — losing the authenticator means contacting an org Owner or Admin to re-enrol you.',
  backToSignInLabel: '‹ Back to sign in',
  rejectedCopy: "That code didn't work. Check it and try again.",
  expiredCopy: 'That code expired. Start again.',
} as const

export const A_MFAENROL_COPY = {
  heading: 'Set up two-factor authentication',
  lead: 'Every account needs a second factor before it can sign in — there is no way to skip this, for any role. Scan the code below with an authenticator app.',
  reasonTemplate: (email: string) =>
    `You reached this screen because ${email} has no authenticator enrolled yet. It is reached from signing in and from nowhere else.`,
  codeFieldLabel: "Enter the 6-digit code your app shows",
  submitButton: 'Confirm and finish setup',
  noRecoveryCodeNote: 'There is no recovery-code fallback. If this authenticator is ever lost, an Owner or Admin on your organisation re-enrols you — there is no self-service reset.',
  rejectedCopy: "That code didn't work. Check it and try again.",
} as const

export const A_FORGOT_COPY = {
  heading: 'Reset your password',
  lead: "Enter your work email and we'll send you a six-digit code.",
  emailLabel: 'Work email',
  submitButton: 'Send the code',
  nonEnumeratingNote: 'The response is identical whether or not an account exists, so this page cannot be used to enumerate users.',
  backToSignInLabel: '‹ Back to sign in',
} as const

export const A_RESET_COPY = {
  heading: 'Check your email',
  leadTemplate: (email: string) => `If an account exists for ${email}, a six-digit code is on its way. The code is valid for one hour and can be used once.`,
  infoNote: 'Resetting your portal password does not affect your Bitbucket API token. Backups keep running.',
  nonEnumeratingNote: 'The message is identical whether or not the account exists, so this page cannot be used to enumerate users.',
  submitButton: 'I have the code',
  resendPrefix: "Didn't get it? ",
  resendLinkLabel: 'Send it again',
} as const

export const A_RESETNEW_COPY = {
  heading: 'Set a new password',
  leadTemplate: (email: string) => `Enter the code from your email and choose a new password for ${email}.`,
  codeFieldLabel: 'Confirmation code',
  newPasswordLabel: 'New password',
  confirmPasswordLabel: 'Confirm new password',
  passwordRule: 'At least 12 characters, with an upper-case letter, a lower-case letter, a number and a symbol. It cannot be one of your last three passwords.',
  everySessionEndsNote: 'Completing this reset ends every session — this device and any other. Sign in again with your new password and your authenticator.',
  submitButton: 'Set the password and sign in',
  lockoutNote: 'Too many wrong codes locks this step for a short period, and the wait grows with each attempt. The code itself is what that limit protects.',
  rejectedCopy: "That code didn't work. Check it and try again.",
  passwordMismatch: "Those two passwords don't match.",
} as const

export const A_CHANGEPW_COPY = {
  heading: 'Change your password',
  lead: 'This is a standalone action, not a re-authentication — it does not run your MFA challenge again.',
  currentPasswordLabel: 'Current password',
  newPasswordLabel: 'New password',
  confirmPasswordLabel: 'Confirm new password',
  everySessionEndsNote: 'Changing your password ends every session — this device and any other, immediately. You will need to sign in again here.',
  submitButton: 'Change password and sign out everywhere',
  cancelLabel: '‹ Cancel',
  rejectedCopy: 'Your current password is incorrect.',
  passwordMismatch: "Those two passwords don't match.",
} as const

export const A_INVITE_COPY = {
  heading: "You've been invited",
  /** Prototype_v2.html: `<b>{email}</b> was invited to <b>{orgName}</b> as <chip>{roleLabel}</chip>,
   * scoped to <chip>{scope}</chip>.` — assembled in the component from these fixed fragments
   * around the served values, never as one interpolated sentence (so the two chips stay real
   * `Chip` components, not string-flattened). */
  leadWasInvitedTo: ' was invited to ',
  leadAs: ' as ',
  leadScopedTo: ', scoped to ',
  scopeNote: 'This state is reached by the token in the invite link — it is not a screen you navigate to. The invite expires 7 days after it was sent.',
  existingUserStrip: "Reached this page with an account already registered under this email? You'll sign in instead of setting a password — same invite, same token, the existing-user path.",
  passwordLabel: 'Set a password',
  confirmPasswordLabel: 'Confirm password',
  nameLabel: 'Your name',
  passwordRule: 'At least 12 characters, with an upper-case letter, a lower-case letter, a number and a symbol.',
  acceptSignupButton: 'Accept invite and confirm your email',
  orDivider: 'OR',
  acceptSigninButton: 'I already have an account — sign in to accept',
  passwordMismatch: "Those two passwords don't match.",
} as const

export const A_RESENDCONFIRM_COPY = {
  heading: 'Resend confirmation code',
  lead: "Enter the email you signed up with and we'll send a new code.",
  emailLabel: 'Work email',
  submitButton: 'Send new code',
  nonEnumeratingNote: 'The message is identical whether or not the account exists, matching the password-reset screen — this cannot be used to enumerate users either.',
  backToSignInLabel: '‹ Back to sign in',
} as const

export const A_INVITEDEAD_COPY = {
  heading: 'This invite link is no longer valid',
  lead: 'Ask an administrator in your organisation to send you a new invitation.',
  uniformLandingStrip: 'One landing for four causes — the link was never valid, it was revoked, it was already accepted, or it expired. The page does not say which, because saying which tells someone holding a stolen link what they are holding.',
  backToSignInLabel: '‹ Back to sign in',
} as const

export const A_FINISHREG_COPY = {
  heading: 'Name your organisation',
  lead: 'Your account is confirmed, but the organisation it should have created was not written. Re-enter the name you chose at sign-up and we will finish the job.',
  orgNameLabel: 'Organisation name',
  submitButton: 'Finish',
  repairNote: 'You are signed in but not yet attached to an organisation. This is the only screen that state can reach, and every other call returns you here until it is done.',
  notAStepNote: 'This is a repair screen, not a step. Finishing here mints exactly one organisation however many times it is retried.',
} as const

export const A_SESSION_COPY = {
  heading: 'Your session ended',
  lead: 'You were signed out after a period of inactivity. Sign in again to pick up where you left off.',
  submitButton: 'Sign back in',
  noTimerNote: 'There is no countdown and no idle timer in the browser. A signed-in session stays usable while it is being used, and ends after a day away from it.',
} as const

export const A_VERIFY_COPY = {
  heading: 'Address verified',
  /** VerificationResult.yaml: "The sentence the landing renders, formatted by the server" — the
   * lead is `display`, never composed from `email` client-side. Used only if a server response
   * somehow omits `display` (it is optional on the schema; every real response is expected to
   * carry it). */
  fallbackLead: 'This address is now verified.',
  scopeStrip:
    'This page is reached from a link in an email, by someone who may not have a portal account at all — which is why it runs outside the application shell and asks for no sign-in.',
} as const

export const A_VERIFYEXP_COPY = {
  heading: 'This link has expired',
  fallbackLead: 'This link is no longer valid.',
  reinviteStrip:
    'Re-invite, not failed. The address is still listed as pending in the product — a quietly vanishing recipient is the failure the whole notification design exists to prevent.',
} as const

export const A_ORGBLOCKED_COPY = {
  heading: 'Access to this organisation is blocked',
  leadTemplate: (orgName: string) => `Your access to ${orgName} is suspended. You are still signed in, and any other organisation you belong to is unaffected.`,
  errorCodeLabel: 'Error code ORG_BLOCKED. Quote this code to support.',
  nothingDeletedNote: 'Nothing stored has been deleted. Backups are paused, and every snapshot stays exactly where it is. Restoring access restores the product in the state you left it.',
  switchButton: 'Switch to another organisation',
  contactSupportButton: 'Contact support',
  notADeadEndNote: 'A block is scoped to one organisation and evaluated on every request, so your session is intact and the other organisations you belong to are still reachable — switching is the ordinary way out of this screen.',
  signOutInsteadLabel: 'Sign out instead',
} as const
